import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, Twitter, Instagram, Check, Send, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    contact_type: '',
    club_name: '',
    full_name: '',
    email: '',
    phone: '',
    level: '',
    subject: '',
    message: '',
    accept_contact: false,
    newsletter: false
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.accept_contact) {
      setError("Veuillez accepter d'être recontacté.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, formData);
      setSuccess(true);
    } catch (err) {
      setError('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div data-testid="contact-page" className="bg-background">
      {/* Hero */}
      <section className="relative pt-32 pb-16 hero-gradient noise-overlay overflow-hidden" data-testid="contact-hero">
        <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        
        <div className="container-asp relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            className="max-w-3xl mx-auto text-center"
          >
            <span className="text-secondary text-sm font-medium tracking-wider uppercase">
              Contact
            </span>
            <h1 className="text-5xl sm:text-6xl font-bold text-white mt-4 mb-6">
              Parlons de<br />
              <span className="text-gradient">votre projet</span>
            </h1>
            <p className="text-xl text-white/60 leading-relaxed">
              Réponse garantie sous 24h. Sans engagement.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="section-padding bg-background" data-testid="contact-form-section">
        <div className="container-asp">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Card className="card-glass p-8">
                  {success ? (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6">
                        <Check className="w-10 h-10 text-primary" />
                      </div>
                      <h3 className="text-2xl font-bold text-white mb-4">Message envoyé !</h3>
                      <p className="text-white/60 text-lg mb-2">Merci pour votre message.</p>
                      <p className="text-white/50">Nous vous recontacterons sous 24h.</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Contact Type */}
                      <div>
                        <Label className="text-sm text-white/70 mb-2 block">Vous êtes *</Label>
                        <Select value={formData.contact_type} onValueChange={(value) => handleChange('contact_type', value)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="contact-type-select">
                            <SelectValue placeholder="Sélectionnez votre profil" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="club_rugby">Club Rugby</SelectItem>
                            <SelectItem value="autre_sport">Autre Sport</SelectItem>
                            <SelectItem value="investisseur">Investisseur</SelectItem>
                            <SelectItem value="presse">Presse</SelectItem>
                            <SelectItem value="partenaire">Partenaire</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Club Name */}
                      <div>
                        <Label className="text-sm text-white/70 mb-2 block">Nom du club / organisation *</Label>
                        <Input
                          required
                          value={formData.club_name}
                          onChange={(e) => handleChange('club_name', e.target.value)}
                          placeholder="Ex: RC Bordeaux"
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                          data-testid="contact-club-input"
                        />
                      </div>

                      {/* Name & Email */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-white/70 mb-2 block">Nom et prénom *</Label>
                          <Input
                            required
                            value={formData.full_name}
                            onChange={(e) => handleChange('full_name', e.target.value)}
                            placeholder="Jean Dupont"
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                            data-testid="contact-name-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-white/70 mb-2 block">Email *</Label>
                          <Input
                            type="email"
                            required
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            placeholder="jean@club.fr"
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                            data-testid="contact-email-input"
                          />
                        </div>
                      </div>

                      {/* Phone & Level */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-white/70 mb-2 block">Téléphone *</Label>
                          <Input
                            type="tel"
                            required
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            placeholder="06 XX XX XX XX"
                            className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                            data-testid="contact-phone-input"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-white/70 mb-2 block">Niveau (si club)</Label>
                          <Select value={formData.level} onValueChange={(value) => handleChange('level', value)}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="contact-level-select">
                              <SelectValue placeholder="Sélectionnez un niveau" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="amateur">Amateur</SelectItem>
                              <SelectItem value="semi_pro">Semi-Pro</SelectItem>
                              <SelectItem value="pro">Professionnel</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Subject */}
                      <div>
                        <Label className="text-sm text-white/70 mb-2 block">Objet *</Label>
                        <Select value={formData.subject} onValueChange={(value) => handleChange('subject', value)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white" data-testid="contact-subject-select">
                            <SelectValue placeholder="Sélectionnez un objet" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="demo">Demande de démonstration</SelectItem>
                            <SelectItem value="tarif">Question sur les tarifs</SelectItem>
                            <SelectItem value="partenariat">Partenariat</SelectItem>
                            <SelectItem value="presse">Demande presse</SelectItem>
                            <SelectItem value="carrieres">Carrières</SelectItem>
                            <SelectItem value="autre">Autre</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Message */}
                      <div>
                        <Label className="text-sm text-white/70 mb-2 block">Message *</Label>
                        <Textarea
                          required
                          value={formData.message}
                          onChange={(e) => handleChange('message', e.target.value)}
                          placeholder="Décrivez votre projet ou votre demande..."
                          rows={5}
                          className="bg-white/5 border-white/10 text-white placeholder:text-white/30 resize-none"
                          data-testid="contact-message-input"
                        />
                      </div>

                      {/* Checkboxes */}
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="accept_contact"
                            checked={formData.accept_contact}
                            onCheckedChange={(checked) => handleChange('accept_contact', checked)}
                            className="border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            data-testid="contact-accept-checkbox"
                          />
                          <Label htmlFor="accept_contact" className="text-sm text-white/60 leading-relaxed cursor-pointer">
                            J'accepte d'être recontacté par Audio Sports Performance *
                          </Label>
                        </div>
                        <div className="flex items-start gap-3">
                          <Checkbox
                            id="newsletter"
                            checked={formData.newsletter}
                            onCheckedChange={(checked) => handleChange('newsletter', checked)}
                            className="border-white/30 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                            data-testid="contact-newsletter-checkbox"
                          />
                          <Label htmlFor="newsletter" className="text-sm text-white/60 leading-relaxed cursor-pointer">
                            Je souhaite recevoir la newsletter
                          </Label>
                        </div>
                      </div>

                      {error && (
                        <div className="text-red-400 text-sm bg-red-500/10 p-3 rounded-lg border border-red-500/20">
                          {error}
                        </div>
                      )}

                      <Button
                        type="submit"
                        className="w-full btn-secondary py-4 text-base"
                        disabled={loading || !formData.contact_type || !formData.subject}
                        data-testid="contact-submit-btn"
                      >
                        {loading ? (
                          'Envoi en cours...'
                        ) : (
                          <>
                            <Send className="w-5 h-5 mr-2" />
                            Envoyer le message
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </Card>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="space-y-8 sticky top-32"
              >
                {/* Contact Details */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-6">Informations</h3>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm text-white/50">Email</div>
                        <a href="mailto:contact@audiosportsperformance.com" className="text-white hover:text-primary transition-colors">
                          contact@audiosportsperformance.com
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-secondary" />
                      </div>
                      <div>
                        <div className="text-sm text-white/50">Téléphone</div>
                        <a href="tel:+33650184381" className="text-white hover:text-secondary transition-colors">
                          +33 6 50 18 43 81
                        </a>
                      </div>
                    </li>
                    <li className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-accent" />
                      </div>
                      <div>
                        <div className="text-sm text-white/50">Adresse</div>
                        <div className="text-white">Bordeaux, France</div>
                      </div>
                    </li>
                  </ul>
                </div>

                {/* Social Links */}
                <div>
                  <h3 className="text-xl font-semibold text-white mb-6">Réseaux</h3>
                  <div className="flex gap-3">
                    <a
                      href="https://linkedin.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center text-white/50 hover:text-primary hover:bg-white/10 transition-all"
                      aria-label="LinkedIn"
                      data-testid="contact-linkedin"
                    >
                      <Linkedin className="w-5 h-5" />
                    </a>
                    <a
                      href="https://twitter.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center text-white/50 hover:text-primary hover:bg-white/10 transition-all"
                      aria-label="Twitter"
                      data-testid="contact-twitter"
                    >
                      <Twitter className="w-5 h-5" />
                    </a>
                    <a
                      href="https://instagram.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center text-white/50 hover:text-secondary hover:bg-white/10 transition-all"
                      aria-label="Instagram"
                      data-testid="contact-instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                  </div>
                </div>

                {/* Response Card */}
                <Card className="card-glass p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                      <Check className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <div className="font-semibold text-white">Réponse sous 24h</div>
                      <div className="text-white/50 text-sm">Sans engagement</div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
